import type { ProjectInvitation, ProjectMember, ProjectRole } from '@bigint-project/cloud-contracts';
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';

import type { CloudApi } from '../api/client';
import { errorMessage } from '../api/client';
import { EmptyMessage, ErrorMessage, Loading } from './Status';

interface SharingPanelProps {
  readonly api: CloudApi;
  readonly projectId: string;
  readonly projectName: string;
  readonly role: ProjectRole;
  readonly onOwnerTransferred: () => void;
}

export function SharingPanel({ api, projectId, projectName, role, onOwnerTransferred }: SharingPanelProps): ReactElement {
  const [members, setMembers] = useState<readonly ProjectMember[]>([]);
  const [invitations, setInvitations] = useState<readonly ProjectInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'editor' | 'viewer'>('editor');
  const [transferTarget, setTransferTarget] = useState('');
  const [transferConfirmation, setTransferConfirmation] = useState('');
  const [previousOwnerRole, setPreviousOwnerRole] = useState<'editor' | 'viewer'>('editor');
  const [busy, setBusy] = useState(false);
  const [invitationLink, setInvitationLink] = useState<string | null>(null);
  const [linkNotice, setLinkNotice] = useState<string | null>(null);
  const canManage = role === 'owner';

  const reload = async (): Promise<void> => {
    const [nextMembers, nextInvitations] = await Promise.all([
      api.listMembers(projectId),
      api.listInvitations(projectId),
    ]);
    setMembers(nextMembers);
    setInvitations(nextInvitations);
  };

  useEffect(() => {
    let cancelled = false;
    void Promise.all([api.listMembers(projectId), api.listInvitations(projectId)]).then(
      ([nextMembers, nextInvitations]) => {
        if (!cancelled) {
          setMembers(nextMembers);
          setInvitations(nextInvitations);
        }
      },
      (reason: unknown) => {
        if (!cancelled) setError(errorMessage(reason));
      },
    ).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [api, projectId]);

  const invite = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!canManage || email.trim().length === 0) return;
    setBusy(true);
    setError(null);
    setInvitationLink(null);
    setLinkNotice(null);
    try {
      const result = await api.createInvitation(projectId, { email: email.trim(), role: inviteRole });
      if (result.token === null) {
        setLinkNotice('這是冪等重試，安全起見無法再次顯示原邀請 token；若連結已遺失，請撤銷後重新建立。');
      } else {
        const link = new URL(`/invitations/${encodeURIComponent(result.token)}`, window.location.origin).toString();
        setInvitationLink(link);
        setLinkNotice('邀請 token 只在建立時顯示一次，請立即複製並透過安全管道交給受邀者。');
      }
      setEmail('');
      await reload();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };

  const copyInvitationLink = async (): Promise<void> => {
    if (invitationLink === null) return;
    try {
      await navigator.clipboard.writeText(invitationLink);
      setLinkNotice('邀請連結已複製。');
    } catch {
      setLinkNotice('瀏覽器無法自動複製；請手動選取下方完整連結。');
    }
  };

  const mutateAndReload = async (action: () => Promise<unknown>): Promise<void> => {
    if (!canManage) return;
    setBusy(true);
    setError(null);
    try {
      await action();
      await reload();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };

  const transfer = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!canManage || transferTarget.length === 0 || transferConfirmation !== projectName) return;
    setBusy(true);
    setError(null);
    try {
      await api.transferOwner(projectId, {
        newOwnerUserId: transferTarget,
        previousOwnerRole,
        confirmation: transferConfirmation,
      });
      onOwnerTransferred();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(false);
    }
  };

  const transferableMembers = members.filter((member) => member.role !== 'owner');

  return (
    <section className="panel" aria-labelledby="sharing-heading">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Project ACL</p>
          <h2 id="sharing-heading">成員與邀請</h2>
        </div>
        {!canManage && <span className="muted">唯讀：只有 Owner 能管理成員</span>}
      </div>
      {error !== null && <ErrorMessage message={error} />}
      {loading && <Loading />}

      <div className="two-column">
        <div>
          <h3>目前成員</h3>
          {!loading && members.length === 0 && <EmptyMessage>沒有成員資料。</EmptyMessage>}
          <ul className="member-list">
            {members.map((member) => (
              <li key={member.userId}>
                <div>
                  <strong>{member.displayName}</strong>
                  <span className="muted">{member.email}</span>
                </div>
                <div className="member-actions">
                  {member.role === 'owner' ? (
                    <span className="role-badge role-owner">owner</span>
                  ) : (
                    <select
                      aria-label={`變更 ${member.displayName} 的角色`}
                      value={member.role}
                      disabled={!canManage || busy}
                      onChange={(event) => void mutateAndReload(() => api.changeMemberRole(
                        projectId,
                        member.userId,
                        { role: event.target.value as 'editor' | 'viewer' },
                      ))}
                    >
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  )}
                  <button
                    type="button"
                    className="danger-link"
                    disabled={!canManage || busy || member.role === 'owner'}
                    onClick={() => void mutateAndReload(() => api.removeMember(projectId, member.userId))}
                  >
                    移除
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3>邀請</h3>
          <form className="stack-form" onSubmit={(event) => void invite(event)}>
            <label htmlFor="invite-email">已驗證的電子郵件</label>
            <input
              id="invite-email"
              type="email"
              value={email}
              disabled={!canManage || busy}
              onChange={(event) => setEmail(event.target.value)}
            />
            <label htmlFor="invite-role">角色</label>
            <select
              id="invite-role"
              value={inviteRole}
              disabled={!canManage || busy}
              onChange={(event) => setInviteRole(event.target.value as 'editor' | 'viewer')}
            >
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
            <button type="submit" disabled={!canManage || busy || email.trim().length === 0}>建立邀請連結</button>
          </form>
          {linkNotice !== null && <p className="invitation-notice" role="status">{linkNotice}</p>}
          {invitationLink !== null && (
            <div className="invitation-link">
              <label htmlFor="invitation-link-value">一次性邀請連結</label>
              <input id="invitation-link-value" readOnly value={invitationLink} onFocus={(event) => event.currentTarget.select()} />
              <button type="button" className="secondary" onClick={() => void copyInvitationLink()}>複製連結</button>
            </div>
          )}
          <ul className="invitation-list">
            {invitations.map((invitation) => (
              <li key={invitation.id}>
                <span>{invitation.invitedEmail} · {invitation.role}</span>
                <button
                  type="button"
                  className="danger-link"
                  disabled={!canManage || busy || invitation.revokedAt !== null || invitation.acceptedAt !== null}
                  onClick={() => void mutateAndReload(() => api.revokeInvitation(projectId, invitation.id))}
                >
                  撤銷
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <form className="owner-transfer" onSubmit={(event) => void transfer(event)}>
        <h3>轉移 Owner</h3>
        <p className="muted">這是原子操作。請選擇現有成員並輸入完整專案名稱「{projectName}」。</p>
        <select
          aria-label="新 Owner"
          value={transferTarget}
          disabled={!canManage || busy}
          onChange={(event) => setTransferTarget(event.target.value)}
        >
          <option value="">選擇成員</option>
          {transferableMembers.map((member) => <option key={member.userId} value={member.userId}>{member.displayName}</option>)}
        </select>
        <input
          aria-label="Owner 轉移確認文字"
          value={transferConfirmation}
          disabled={!canManage || busy}
          placeholder={projectName}
          onChange={(event) => setTransferConfirmation(event.target.value)}
        />
        <select
          aria-label="原 Owner 轉移後角色"
          value={previousOwnerRole}
          disabled={!canManage || busy}
          onChange={(event) => setPreviousOwnerRole(event.target.value as 'editor' | 'viewer')}
        >
          <option value="editor">原 Owner 成為 Editor</option>
          <option value="viewer">原 Owner 成為 Viewer</option>
        </select>
        <button
          type="submit"
          className="danger"
          disabled={!canManage || busy || transferTarget.length === 0 || transferConfirmation !== projectName}
        >
          轉移 Owner
        </button>
      </form>
    </section>
  );
}
